import React from 'react';
import {Typography, Box, Accordion, AccordionDetails, AccordionSummary, styled, Button} from '@mui/material';
// When upgrading to mui v6, this will require updating.
// When upgrading to v7, some more.
import Grid from '@mui/material/Unstable_Grid2';
import {ModeEdit, KeyboardArrowDown} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {capitalize} from '../../../utils/helpers';

// TODO: Everything in review should probably use `experiment` information
// instead of `formData`. I think we want to show what we currently have on the
// database side.

interface ExperimentDetailsProps {
    experiment: any;
    handleEdit: () => void;
}

const ExperimentDetails: React.FC<ExperimentDetailsProps> = ({experiment, handleEdit}) => {
    return (
        <Accordion defaultExpanded sx={{bgcolor: 'on-primary', boxShadow: 'none'}}>
            <AccordionSummary
                expandIcon={<KeyboardArrowDown />}
                aria-controls="experiment-details-content"
                id="experiment-details-header"
                sx={{
                    flexDirection: 'row-reverse',
                    width: '100%',
                    bgcolor: 'surface-container-high',
                    borderRadius: '12px 12px 0 0'
                }}>
                <Box ml={2} my={0} py={0} sx={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <Typography variant="title" size="medium">
                        Experiment details
                    </Typography>
                    <span style={{display: 'flex'}} onClick={handleEdit}>
                        <ModeEdit color="primary" sx={{fontSize: '18px', margin: '0 5px'}} />
                        <Typography variant="label" size="large" color={'primary.main'}>
                            Edit
                        </Typography>
                    </span>
                </Box>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    border: '1px solid',
                    borderColor: 'surface-container-highest',
                    borderRadius: '0 0 8px 8px',
                    borderTop: 'none',
                    width: '100%'
                }}>
                <Grid container spacing={1}>
                    <Grid xs={6}>
                        <Typography variant="title" size="medium">
                            Name
                        </Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography variant="body" size="large">
                            {experiment?.name}
                        </Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography variant="title" size="medium">
                            Description
                        </Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography>{experiment?.description || 'N/A'}</Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography variant="title" size="medium">
                            Sequencing type
                        </Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography>{experiment?.sequencing_type}</Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography variant="title" size="medium">
                            Organism
                        </Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography>{experiment?.organism}</Typography>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

interface SequencingDetailsProps {
    formData: any;
    handleEdit: () => void;
}

const SequencingDetails: React.FC<SequencingDetailsProps> = ({formData, handleEdit}) => {
    // Show different properties based on the sequencing type.
    const properties = [];
    if (formData.experiment_details?.sequencing_type === 'RNA Seq') {
        properties.push(
            'analyzed_molecule',
            'rna_selection_method',
            'sequencing_adapter',
            'sequencing_platform',
            'platform_model',
            'sequencing_read_type',
            'sequencing_sense',
            'files_per_sample'
        );
    }
    if (['ChIP Seq', 'ATAC Seq'].includes(formData.experiment_details?.sequencing_type)) {
        properties.push('sequencing_platform', 'platform_model', 'sequencing_read_type', 'files_per_sample');
    }

    return (
        <Accordion defaultExpanded sx={{bgcolor: 'on-primary', boxShadow: 'none'}}>
            <AccordionSummary
                expandIcon={<KeyboardArrowDown />}
                aria-controls="experiment-details-content"
                id="experiment-details-header"
                sx={{
                    flexDirection: 'row-reverse',
                    width: '100%',
                    bgcolor: 'surface-container-high',
                    borderRadius: '12px 12px 0 0'
                }}>
                <Box ml={2} my={0} py={0} sx={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <Typography variant="title" size="medium">
                        Sequencing details
                    </Typography>
                    <span style={{display: 'flex'}} onClick={handleEdit}>
                        <ModeEdit color="primary" sx={{fontSize: '18px', margin: '0 5px'}} />
                        <Typography variant="label" size="large" color={'primary.main'}>
                            Edit
                        </Typography>
                    </span>
                </Box>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    border: '1px solid',
                    borderColor: 'surface-container-highest',
                    borderRadius: '0 0 8px 8px',
                    borderTop: 'none',
                    width: '100%'
                }}>
                <Grid container spacing={1}>
                    {properties.map((k: string) => {
                        return (
                            <React.Fragment key={`sequencing-details-${k}`}>
                                <Grid xs={6} key={`${k}-key`}>
                                    <Typography variant="title" size="medium">
                                        {k.split('_').map(capitalize).join(' ')}
                                    </Typography>
                                </Grid>
                                <Grid xs={6} key={`${k}-value`}>
                                    <Typography variant="body" size="large">
                                        {k === 'sequencing_sense' && formData.sequencing_details[k] == ''
                                            ? 'unstranded'
                                            : formData.sequencing_details[k]}
                                    </Typography>
                                </Grid>
                            </React.Fragment>
                        );
                    })}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

interface SampleOverviewProps {
    experiment: any;
    formData: any;
    samples: any[];
    handleEdit: () => void;
}

const SampleOverview: React.FC<SampleOverviewProps> = ({experiment, formData, samples, handleEdit}) => {
    return (
        <Accordion defaultExpanded sx={{bgcolor: 'on-primary', boxShadow: 'none'}}>
            <AccordionSummary
                expandIcon={<KeyboardArrowDown />}
                aria-controls="experiment-details-content"
                id="experiment-details-header"
                sx={{
                    flexDirection: 'row-reverse',
                    width: '100%',
                    bgcolor: 'surface-container-high',
                    borderRadius: '12px 12px 0 0'
                }}>
                <Box ml={2} my={0} py={0} sx={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <Typography variant="title" size="medium">
                        Sample details
                    </Typography>
                    <span style={{display: 'flex'}} onClick={handleEdit}>
                        <ModeEdit color="primary" sx={{fontSize: '18px', margin: '0 5px'}} />
                        <Typography variant="label" size="large" color={'primary.main'}>
                            Edit
                        </Typography>
                    </span>
                </Box>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    border: '1px solid',
                    borderColor: 'surface-container-highest',
                    borderRadius: '0 0 8px 8px',
                    borderTop: 'none',
                    width: '100%'
                }}>
                <Grid container spacing={1}>
                    <Grid xs={6}>
                        <Typography variant="title" size="medium">
                            Number of Samples
                        </Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography>{experiment?.sample_count}</Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography variant="title" size="medium">
                            Files
                        </Typography>
                    </Grid>
                    <Grid xs={6}>
                        <Typography>
                            {samples?.reduce((total: number, sample: any) => {
                                return total + (sample.files ? sample.files.length : 0);
                            }, 0)}
                        </Typography>
                    </Grid>
                    {['ChIP Seq', 'ATAC Seq'].includes(experiment?.sequencing_type) &&
                        // Also just this for RNA?
                        Object.entries(formData.step_three_details)?.map(([key, value]: [string, any]) => {
                            return (
                                <React.Fragment key={`chip-metadata-${key}`}>
                                    <Grid xs={6} key={`${key}-key`}>
                                        <Typography variant="title" size="medium">
                                            {key.split('_').map(capitalize).join(' ')}
                                        </Typography>
                                    </Grid>
                                    <Grid xs={6} key={`${key}-value`}>
                                        <Typography>{value.join('; ')}</Typography>
                                    </Grid>
                                </React.Fragment>
                            );
                        })}
                    {experiment?.sequencing_type === 'RNA Seq' && (
                        <>
                            <Grid xs={6}>
                                <Typography variant="title" size="medium">
                                    Variables
                                </Typography>
                            </Grid>
                            <Grid xs={6}>
                                <Typography>{Object.keys(formData.step_three_details)?.join('; ')}</Typography>
                            </Grid>
                            <Grid xs={6}>
                                <Typography variant="title" size="medium">
                                    Values
                                </Typography>
                            </Grid>
                            <Grid xs={6}>
                                <Typography>{Object.values(formData.step_three_details).flat().join('; ')}</Typography>
                            </Grid>
                        </>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

interface Step5Props {
    experiment: any;
    formData: any;
    samples: any[];
    setStep: (step: number) => void;
    submitExperiment: () => void;
}

/**
 * Step 5 of the experiment creation process.
 *
 * Lets the user review the various details of the experiment and quickly
 * access the steps to edit them.
 */
const Step5: React.FC<Step5Props> = ({experiment, formData, samples, setStep, submitExperiment}) => {
    const navigate = useNavigate();

    return (
        <Box>
            <ExperimentDetails experiment={experiment} handleEdit={() => setStep(0)} />
            <SequencingDetails formData={formData} handleEdit={() => setStep(1)} />
            <SampleOverview experiment={experiment} formData={formData} samples={samples} handleEdit={() => setStep(3)} />
            {experiment?.sequencing_type !== 'RNA Seq' && (
                <Typography variant="body" size="small" sx={{color: 'on-surface-variant', mb: 2}}>
                    *Clicking Save and Continue will auto direct you to Setup Analysis page
                </Typography>
            )}
            <Button variant="contained" color="primary" onClick={submitExperiment} fullWidth>
                Save and Continue
            </Button>
            <Button variant="text" color="primary" onClick={() => navigate(`/`)} fullWidth>
                Save Progress and Exit
            </Button>
        </Box>
    );
};
export default Step5;
